import { success, failure } from '../utils/result-pattern'

class Auth {
  constructor(context) {
    this.context = context
  }

  /**
   * authenticate with email and password
   * @param {string} email - user email
   * @param {string} password - user password
   * @returns {Promise<object>} result indicating success or failure
   */
  async password(email, password) {
    try {
      const response = await fetch('/api/auth/sign-in', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password
        })
      })

      const result = await response.json()
      if (!response.ok) {
        return failure(result)
      }

      return success(result)
    } catch (error) {
      return failure({ error: 'network_error', error_description: error.message })
    }
  }

  /**
   * authenticate with Google oauth using redirect flow
   * @returns {Promise<object>} result indicating success or failure
   */
  google() {
    try {
      const clientId = this.context.$config.googleClientId
      if (!clientId) {
        return failure('the Google client id was not configured')
      }

      // construct google oauth url
      const redirectUri = `${window.location.origin}/auth/google`
      const scope = 'openid email profile'
      const responseType = 'code'
      const state = Math.random().toString(36).substring(2, 15)

      // store state in session storage for verification
      sessionStorage.setItem('oauth_state', state)

      // redirect to google oauth
      window.location.href =
        `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(clientId)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=${responseType}&` +
        `state=${state}`

      return success()
    } catch (error) {
      return failure(error.message)
    }
  }

  /**
   * authenticate with linkedin oauth using redirect flow
   * @returns {Promise<object>} result indicating success or failure
   */
  linkedin() {
    try {
      const clientId = this.context.$config.linkedInClientId
      if (!clientId) {
        return failure('the LinkedIn client id was not configured')
      }

      // construct linkedin oauth url
      const redirectUri = `${window.location.origin}/auth/linkedin`
      const scope = 'r_emailaddress r_liteprofile'
      const responseType = 'code'
      const state = Math.random().toString(36).substring(2, 15)

      // store state in session storage for verification
      sessionStorage.setItem('linkedin_oauth_state', state)

      // redirect to linkedin oauth
      window.location.href =
        `https://www.linkedin.com/oauth/v2/authorization?` +
        `client_id=${encodeURIComponent(clientId)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=${responseType}&` +
        `state=${state}`

      return success()
    } catch (error) {
      return failure(error.message)
    }
  }

  /**
   * sign out user
   * @returns {Promise<object>} result indicating success or failure
   */
  async signout() {
    try {
      const response = await fetch('/api/auth/sign-out', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      // clear user state
      this.context.store.commit('user/SET_USER', null)
      this.context.store.commit('cart/SET_ITEMS', [])

      // emit auth change event
      if (typeof window !== 'undefined' && window.$nuxt) {
        window.$nuxt.$emit('on-auth-change', false)
      }

      if (!response.ok) {
        return failure()
      }

      const data = await response.json()
      return success(data)
    } catch (error) {
      return failure({ error: 'network_error', error_description: error.message })
    }
  }

  /**
   * refresh authentication tokens
   * @returns {Promise<object>} result indicating success or failure
   */
  async refresh() {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        return failure()
      }

      const result = await response.json()
      return success(result)
    } catch (error) {
      return failure({ error: 'network_error', error_description: error.message })
    }
  }

  /**
   * get current user information
   * @returns {Promise<object>} result containing user info or error
   */
  async me() {
    try {
      const response = await fetch('/api/auth/me', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        return failure()
      }

      const result = await response.json()
      this.context.store.set('user/user', result)

      return success(result)
    } catch (error) {
      return failure({
        error: 'network_error',
        error_description: error.message
      })
    }
  }
}

export default (context, inject) => {
  const auth = new Auth(context)

  inject('auth', auth)
  context.$auth = auth
}
